import {
  Markup,
  PointMarkup,
  PolygonMeasurement,
} from '@technology/web3d-plugin-markup';
import { RGBFormat } from '@trimble/utilities';
import { rgbToWeb3dColor } from '@trimble/web3d-utils';
import {
  LineStyle,
  Measurement,
  MeasurementType,
  PointSymbol,
} from './measurement';

/**
 * Interface for storing last used styles per measurement type.
 */
interface LastUsedStyle {
  color?: RGBFormat;
  pointSymbol?: PointSymbol;
  lineThickness?: number;
  lineStyle?: LineStyle;
  fillOpacity?: number;
}

/**
 * Manages last used styles for measurements.
 * Tracks color, point symbol, line thickness, line style, and
 * fill opacity preferences per measurement type.
 */
export class MeasurementStyleManager {
  private readonly lastUsedStyles = new Map<MeasurementType, LastUsedStyle>();

  /**
   * Updates the last used style based on changes between current and updated measurements.
   * @param updatedMeasurement - The updated measurement
   * @param currentMeasurement - The current measurement before update
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity -- MERCATOR-713: complexity 39 > 15 allowed; refactor later
  public updateLastUsedStyle(
    updatedMeasurement: Measurement,
    currentMeasurement: Measurement
  ): void {
    const currentColor = currentMeasurement.color;
    const updatedColor = updatedMeasurement.color;
    const colorChanged =
      JSON.stringify(currentColor) !== JSON.stringify(updatedColor);

    let symbolChanged = false;
    let lineThicknessChanged = false;
    let lineStyleChanged = false;
    let fillOpacityChanged = false;
    if (updatedMeasurement.type === 'point') {
      const currentSymbol =
        'pointSymbol' in currentMeasurement
          ? currentMeasurement.pointSymbol
          : undefined;
      const updatedSymbol =
        'pointSymbol' in updatedMeasurement
          ? updatedMeasurement.pointSymbol
          : undefined;
      symbolChanged =
        JSON.stringify(currentSymbol) !== JSON.stringify(updatedSymbol);
    } else if (
      updatedMeasurement.type === 'area' ||
      updatedMeasurement.type === 'volume'
    ) {
      const currentLineThickness =
        'lineThickness' in currentMeasurement
          ? currentMeasurement.lineThickness
          : undefined;
      const updatedLineThickness =
        'lineThickness' in updatedMeasurement
          ? updatedMeasurement.lineThickness
          : undefined;
      lineThicknessChanged =
        JSON.stringify(currentLineThickness) !==
        JSON.stringify(updatedLineThickness);

      const currentLineStyle =
        'lineStyle' in currentMeasurement
          ? currentMeasurement.lineStyle
          : undefined;
      const updatedLineStyle =
        'lineStyle' in updatedMeasurement
          ? updatedMeasurement.lineStyle
          : undefined;
      lineStyleChanged =
        JSON.stringify(currentLineStyle) !== JSON.stringify(updatedLineStyle);
    }

    if (
      updatedMeasurement.type === 'area' ||
      updatedMeasurement.type === 'volume'
    ) {
      const currentFillOpacity =
        'fillOpacity' in currentMeasurement
          ? currentMeasurement.fillOpacity
          : undefined;
      const updatedFillOpacity =
        'fillOpacity' in updatedMeasurement
          ? updatedMeasurement.fillOpacity
          : undefined;
      fillOpacityChanged =
        JSON.stringify(currentFillOpacity) !==
        JSON.stringify(updatedFillOpacity);
    }

    // Only update lastUsedStyles if color, symbol, line thickness, line style, or fill opacity actually changed
    if (
      colorChanged ||
      symbolChanged ||
      lineThicknessChanged ||
      lineStyleChanged ||
      fillOpacityChanged
    ) {
      // Get existing lastUsedStyle to preserve unchanged properties
      const existingStyle = this.lastUsedStyles.get(updatedMeasurement.type);
      const lastUsedStyle: LastUsedStyle = {
        ...existingStyle,
      };

      // Update color only if it changed
      if (colorChanged && updatedMeasurement.color) {
        lastUsedStyle.color = updatedMeasurement.color;
      }

      // Update symbol only if it changed (for point measurements)
      if (
        symbolChanged &&
        updatedMeasurement.type === 'point' &&
        'pointSymbol' in updatedMeasurement
      ) {
        lastUsedStyle.pointSymbol = updatedMeasurement.pointSymbol;
      }

      // Update line thickness and line style when they change (area + volume boundary)
      if (
        updatedMeasurement.type === 'area' ||
        updatedMeasurement.type === 'volume'
      ) {
        if (lineThicknessChanged && 'lineThickness' in updatedMeasurement) {
          lastUsedStyle.lineThickness = updatedMeasurement.lineThickness;
        }
        if (lineStyleChanged && 'lineStyle' in updatedMeasurement) {
          lastUsedStyle.lineStyle = updatedMeasurement.lineStyle;
        }
      }

      if (
        (updatedMeasurement.type === 'area' ||
          updatedMeasurement.type === 'volume') &&
        fillOpacityChanged &&
        'fillOpacity' in updatedMeasurement
      ) {
        lastUsedStyle.fillOpacity = updatedMeasurement.fillOpacity;
      }

      this.lastUsedStyles.set(updatedMeasurement.type, lastUsedStyle);
    }
  }

  public getLastUsedColor(type: MeasurementType): RGBFormat | undefined {
    return this.lastUsedStyles.get(type)?.color;
  }

  public setLastUsedColor(type: MeasurementType, color: RGBFormat): void {
    const existing = this.lastUsedStyles.get(type) ?? {};
    this.lastUsedStyles.set(type, { ...existing, color });
  }

  public getLastUsedFillOpacity(type: MeasurementType): number | undefined {
    return this.lastUsedStyles.get(type)?.fillOpacity;
  }

  public setLastUsedFillOpacity(
    type: MeasurementType,
    fillOpacity: number
  ): void {
    const existing = this.lastUsedStyles.get(type) ?? {};
    this.lastUsedStyles.set(type, { ...existing, fillOpacity });
  }

  public getLastUsedLineThickness(type: MeasurementType): number | undefined {
    return this.lastUsedStyles.get(type)?.lineThickness;
  }

  public getLastUsedLineStyle(type: MeasurementType): LineStyle | undefined {
    return this.lastUsedStyles.get(type)?.lineStyle;
  }

  public setLastUsedLineThickness(
    type: MeasurementType,
    lineThickness: number
  ): void {
    const existing = this.lastUsedStyles.get(type) ?? {};
    this.lastUsedStyles.set(type, { ...existing, lineThickness });
  }

  public setLastUsedLineStyle(
    type: MeasurementType,
    lineStyle: LineStyle
  ): void {
    const existing = this.lastUsedStyles.get(type) ?? {};
    this.lastUsedStyles.set(type, { ...existing, lineStyle });
  }

  /**
   * Applies the last used style to a measurement and its markup.
   * @param measurement - The measurement to apply style to
   * @param markup - The markup to apply style to
   */
  public applyLastUsedStyle(measurement: Measurement, markup: Markup): void {
    const lastUsedStyle = this.lastUsedStyles.get(measurement.type);
    if (!lastUsedStyle || !markup) {
      return;
    }

    if (lastUsedStyle.color) {
      measurement.color = lastUsedStyle.color;
      markup.color = rgbToWeb3dColor(lastUsedStyle.color);
    }

    if (measurement.type === 'point' && lastUsedStyle.pointSymbol) {
      measurement.pointSymbol = lastUsedStyle.pointSymbol;
      const pointMarkup = markup as PointMarkup;
      pointMarkup.imageUrl = lastUsedStyle.pointSymbol.url;
      pointMarkup.imagePosition = lastUsedStyle.pointSymbol.position;
    }

    if (measurement.type === 'area' || measurement.type === 'volume') {
      const polygonMeasurement = markup as PolygonMeasurement;
      if (lastUsedStyle.lineThickness !== undefined) {
        measurement.lineThickness = lastUsedStyle.lineThickness;
        polygonMeasurement.lineThickness = lastUsedStyle.lineThickness;
      }
      if (lastUsedStyle.lineStyle !== undefined) {
        measurement.lineStyle = lastUsedStyle.lineStyle;
        polygonMeasurement.lineStyle = lastUsedStyle.lineStyle;
      }
      if (lastUsedStyle.fillOpacity !== undefined) {
        measurement.fillOpacity = lastUsedStyle.fillOpacity;
        if (measurement.type === 'area') {
          polygonMeasurement.fillOpacity = lastUsedStyle.fillOpacity;
        }
      }
    }
  }
}
